import { toLatLng, distanceMiles } from '../../shared/models';

// Coordinates of the restaurant / pickup point. Handles both the `orders`
// doc shape (restaurantLocation.coordinates) and the accepted-order
// subcollection shape (pickupLocation).
export function restaurantCoords(order) {
  if (!order) return null;
  return (
    toLatLng(order.pickupLocation) ||
    toLatLng(order.restaurantLocation?.coordinates) ||
    toLatLng(order.restaurantLocation) ||
    toLatLng(order.raw?.pickupLocation) ||
    null
  );
}

// Coordinates of the customer / drop-off point.
export function customerCoords(order) {
  if (!order) return null;
  return (
    toLatLng(order.deliveryLocation) ||
    toLatLng(order.deliveryAddress?.coordinates) ||
    toLatLng(order.deliveryAddress?.location) ||
    toLatLng(order.customerLocation?.coordinates) ||
    toLatLng(order.customerLocation) ||
    toLatLng(order.raw?.deliveryLocation) ||
    null
  );
}

// Human-readable delivery address string.
export function deliveryAddressText(order) {
  if (!order) return '';
  const a = order.deliveryAddress;
  if (a && typeof a === 'object') {
    const s = [a.street, a.city, a.parish || a.state].filter(Boolean).join(', ');
    if (s) return s;
  } else if (typeof a === 'string' && a) {
    return a;
  }
  return order.customerAddress || '';
}

// Restaurant address string (only present on the orders-doc shape).
export function restaurantAddressText(order) {
  if (!order) return '';
  const r = order.restaurantLocation;
  if (r && typeof r === 'object') return r.address || r.street || '';
  return order.pickupAddress || '';
}

// Mirrors iOS ETAManager.maxDeliveryRadiusMiles — the max straight-line
// radius (miles) a vehicle type may be offered. null = unlimited.
export function maxDeliveryRadiusMiles(vehicleType) {
  switch ((vehicleType || '').toLowerCase()) {
    case 'walking': return 0.5;
    case 'bicycle': return 1.0;
    default: return null;
  }
}

// Mirrors iOS DriverDashboardViewModel.isWithinVehicleRange — both legs
// (driver -> restaurant to pick up, restaurant -> customer to deliver)
// must fit the vehicle's radius. A leg whose coordinates are unknown
// doesn't disqualify the order.
export function isWithinVehicleRange(order, vehicleType, driverPos) {
  const maxMiles = maxDeliveryRadiusMiles(vehicleType);
  if (maxMiles == null) return true;
  const rest = restaurantCoords(order);
  const drop = customerCoords(order);
  const delivery = distanceMiles(rest, drop);
  if (delivery != null && delivery > maxMiles) return false;
  const pickup = distanceMiles(driverPos, rest);
  if (pickup != null && pickup > maxMiles) return false;
  return true;
}

// A courier counts as "active" only if it wrote a location/status inside
// this window — catches drivers whose app died without flipping isOnline.
export const COURIER_ACTIVE_WINDOW_MS = 10 * 60 * 1000;
const SMALL_VEHICLES = new Set(['walking', 'bicycle']);
const SHORT_ORDER_MILES = 1.0;

// activeCouriers: Map(driverId -> { vehicleType, seen (ms) }) — online
// walking/bicycle couriers from the drivers listener. With legMiles given,
// only counts couriers whose radius actually covers the order — a 0.9-mile
// drop isn't held for a walker (0.5-mi range), but it is for a cyclist (1 mi).
export function hasActiveSmallVehicleCourier(activeCouriers, now = Date.now(), legMiles = null) {
  if (!activeCouriers) return false;
  for (const c of activeCouriers.values()) {
    if (!SMALL_VEHICLES.has((c.vehicleType || '').toLowerCase())) continue;
    if (now - c.seen >= COURIER_ACTIVE_WINDOW_MS) continue;
    if (legMiles == null || (maxDeliveryRadiusMiles(c.vehicleType) ?? 0) >= legMiles) return true;
  }
  return false;
}

// Mirrors iOS DriverDashboardViewModel.canSeeOrder:
// - walking/bicycle couriers: capability filter on both legs
// - motorized drivers: everything EXCEPT sub-1-mile orders while a
//   small-vehicle courier is active (those are reserved for them)
export function canSeeAvailableOrder(order, vehicleType, driverPos, activeCouriers, now = Date.now()) {
  if (maxDeliveryRadiusMiles(vehicleType) != null) {
    return isWithinVehicleRange(order, vehicleType, driverPos);
  }
  const leg = distanceMiles(restaurantCoords(order), customerCoords(order));
  if (leg != null && leg <= SHORT_ORDER_MILES &&
      hasActiveSmallVehicleCourier(activeCouriers, now, leg)) {
    return false;
  }
  return true;
}

// External navigation URLs — iOS navigationApp picker options:
// Apple Maps / Google Maps.
export function navLinks(coords, name = '', address = '') {
  if (!coords) return null;
  const ll = `${coords.lat},${coords.lng}`;
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${ll}`,
    apple: `https://maps.apple.com/?daddr=${ll}`,
  };
}
